import {
  getBlogBySlug,
  getCommunities,
  getRecommendations,
  getSubscribersCountBySlug,
} from "@/lib/api/blogs"
import { BlogAndSubscribeDetails, BlogDetails, PageParams } from "./types"
import {
  Blog,
  BlogRecommendation,
  CommunityEntryRes,
  isErrorResponse,
  Subscription,
} from "@/types/types"
import { getUsersSubscriptionToBlog } from "@/lib/api/users"
import { getBlogUrl } from "@/lib/utils/blog"

/**
 * Maps the different states of the subscribe modal to the paths they should be
 * rendered at. We use intercepting routes to render the correct component for the
 * current state, so instead of keeping track of state in client components, we
 * just navigate to the appropriate path.
 *
 * NOTE: You cannot prefetch the ENTER_EMAIL route from any buttons because for logged in users that have an email,
 * it will trigger the onComplete in the EnterEmail page and cause the user to auto-subscribe as soon as they
 * navigate to the blog or post page, without even clicking the button.
 */
export const enum ModalRoute {
  ENTER_EMAIL = "subscribe",
  CHOOSE_COMMUNITIES = "subscribe/choose-communities",
  RECOMMENDATIONS = "subscribe/recommendations",
  SUCCESS = "subscribe/success",
  ALREADY_SUBSCRIBED = "subscribe/already-subscribed",
}

/**
 * Generates the partial modal path for a given route and blog slug
 * @param route - The modal route to generate
 * @param blogSlug - The blog slug to include in the path
 * @returns The partial modal path (doesn't include base URL).
 */
export const getModalPath = (
  route: ModalRoute,
  blog?: Pick<Blog, "custom_domain" | "lowercase_url">,
  email?: string
): string => {
  const blogUrlDetails = getBlogUrl(blog)

  return `${blogUrlDetails.customDomainOrRelativeUrl}${route}${email ? `?${new URLSearchParams({ email }).toString()}` : ""}`
}

/**
 * Fetches the blog data for the initial subscribe modal. Includes the blog slug,
 * name, logo URL, and subscriber count.
 *
 * @param params - The page params. These need to have the blogSlug in there or we will not be able to fetch the blog data.
 * @returns The blog data.
 */
export async function getBlogAndSubscriberData(
  params: PageParams
): Promise<BlogAndSubscribeDetails> {
  const blogSlug = await decodeBlogSlug(params)

  // Fetch the blog data.
  const [blog, subscriberCount] = await Promise.all([
    getBlogBySlug(blogSlug),
    getSubscribersCountBySlug(blogSlug),
  ])

  return {
    id: blog.id,
    lowercase_url: blog.lowercase_url,
    name: blog.name,
    logo_url: blog.logo_url,
    subscriberCount: subscriberCount.total,
    theme: blog.theme,
  }
}

/**
 * Fetches the blog data for the initial subscribe modal. Includes the blog slug,
 * name, logo URL, and theme.
 *
 * @param params - The page params. These need to have the blogSlug in there or we will not be able to fetch the blog data.
 * @returns The blog data.
 */
export async function getBlogData(params: PageParams): Promise<BlogDetails> {
  const blogSlug = await decodeBlogSlug(params)

  // Fetch the blog data.
  const blog = await getBlogBySlug(blogSlug)

  return {
    id: blog.id,
    lowercase_url: blog.lowercase_url,
    name: blog.name,
    logo_url: blog.logo_url,
    theme: blog.theme,
  }
}

export async function getBlogAndBlogCommunities(params: PageParams): Promise<{
  blog: BlogDetails
  communities: CommunityEntryRes[]
}> {
  const blogSlug = await decodeBlogSlug(params)

  // Fetch the blog data.
  const blog = await getBlogBySlug(blogSlug)

  // Fetch the communities for the blog.
  const communities = await getCommunities(blog.id)

  return {
    blog: {
      id: blog.id,
      lowercase_url: blog.lowercase_url,
      name: blog.name,
      logo_url: blog.logo_url,
      theme: blog.theme,
    },
    communities,
  }
}

/**
 * Fetches the blog data and recommendations for the recommendations modal page.
 * @param params - The page params.
 * @returns The blog data and recommendations.
 */
export async function getBlogAndRecommendations(params: PageParams): Promise<{
  blog: Blog
  recommendations: BlogRecommendation[]
  subscribedRecommendations: string[]
}> {
  const blogSlug = await decodeBlogSlug(params)

  // Fetch the blog data.
  const blog = await getBlogBySlug(blogSlug)

  // Fetch the recommendations.
  const recommendations = await getRecommendations(blog.id)

  if (isErrorResponse(recommendations)) {
    console.error("Error fetching recommendations", recommendations)
    throw new Error(recommendations.msg)
  }

  // Fetch subscription statuses for each recommendation.
  const promises = recommendations.map((r) => getUsersSubscriptionToBlog(r.id))
  const subscribedRecommendations = await Promise.all(promises)

  return {
    blog,
    recommendations,
    subscribedRecommendations: subscribedRecommendations
      .filter((r): r is Subscription => !isErrorResponse(r))
      .filter((r) => r && r.status === "ACTIVE")
      .map((r) => r.blogId),
  }
}

/**
 * Decodes the URL-encoded blogSlug from the page params.
 *
 * @param params - The page params.
 * @returns The decoded blogSlug.
 */
async function decodeBlogSlug(params: PageParams): Promise<string> {
  const { blogSlug: encodedSlug } = await params
  return decodeURIComponent(encodedSlug)
}

/**
 * Decodes the URL-encoded email from the query params.
 *
 * @param queryParams - The query params.
 * @returns The decoded email.
 */
export async function decodeEmail(
  searchParams?: Promise<{ email?: string }>
): Promise<string> {
  const email = (await searchParams)?.email

  console.log("email", email)

  return decodeURIComponent(email || "")
}
